import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type {
  Endpoints,
  Features,
  GamificationCoins,
  GamificationConfig,
  Limits,
} from '@chat-emp/shared';

import {
  emitToHost,
  onBridgeMessage,
  scrubQueryFromUrl,
  takeHandoffFromFragment,
} from '../lib/bridge.js';
import {
  ApiRequestError,
  createSession,
  getSessionToken,
  NO_ENDPOINTS,
  NO_FEATURES,
  NO_LIMITS,
  reauthRefused,
  setReauthTrigger,
} from '../lib/session.js';
import { setGamificationCompanyId, setGamificationConfig } from '../lib/gamification.js';

type Status = 'idle' | 'ready' | 'error' | 'expired' | 'unavailable';

interface SessionState {
  sessionReady: boolean;
  status: Status;
  /** The host's coin budget; null until it sends one. */
  gamification: GamificationConfig | null;
  /**
   * What this account may use, as decided by the BFF from the verified identity.
   * Off until a session says otherwise — a gated feature that renders for a
   * frame and then vanishes is worse than one that arrives a moment late.
   *
   * A hint for the UI only. The routes enforce the same answer themselves, so
   * nothing here is load-bearing for access.
   */
  features: Features;
  /**
   * Third-party endpoints the BFF names for us, because they are other apps'
   * hostnames and may not be baked into this bundle. Empty until a session
   * answers, and an empty member means that endpoint is not configured.
   */
  endpoints: Endpoints;
  /** Runtime-tunable numbers the BFF names (see `Limits`). Defaults until a
   *  session answers, so nothing renders off an unconfirmed limit. */
  limits: Limits;
  /**
   * What session replay records under (AI-3016). Empty until a session answers,
   * and empty means "do not record" — a replay filed under a guessed id is
   * worse than none, because it looks fine and joins to nothing.
   *
   * Nothing renders from it. It is here because it is a fact the verified
   * session hands over, and this is where those live.
   */
  analyticsId: string;
}

const SessionContext = createContext<SessionState>({
  sessionReady: false,
  status: 'idle',
  gamification: null,
  features: NO_FEATURES,
  endpoints: NO_ENDPOINTS,
  limits: NO_LIMITS,
  analyticsId: '',
});

/**
 * A coin block is only usable if every field is a real number. Anything else is
 * ignored outright — the web component ignores malformed JSON the same way, and
 * a half-parsed budget would show an amount the host never agreed to pay.
 */
function readCoins(payload: unknown): GamificationCoins | null {
  const coins = (payload as { coins?: unknown } | undefined)?.coins as
    Record<string, unknown> | undefined;
  if (!coins) return null;
  const fields = ['init_chat', 'first_reply', 'limit', 'claimed'] as const;
  const out = {} as GamificationCoins;
  for (const field of fields) {
    const value = coins[field];
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    out[field] = value;
  }
  return out;
}

/** Session readiness for the seam to gate on (getSessionToken stays a lib import). */
export function useSession(): SessionState {
  return useContext(SessionContext);
}

/**
 * Establishes the chat-emp session for both /chat and /widget: redeems the
 * handoff (`init` bridge message for the full page, URL fragment for the widget)
 * into a session JWT, arms the 401 → `session-expired` relay, and announces
 * `ready` to the host once the init listener is live (so a host that waits for
 * `ready` before sending `init` doesn't race us).
 *
 * No Stream connection happens here — that's the lazily-connected StreamProvider's
 * job — so a closed widget still holds a session for cheap BFF reads at zero
 * Stream (MAU/concurrent) cost. The single-use handoff redeems at most once even
 * under StrictMode's double-mount, via createSession's per-token de-dupe.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [sessionReady, setSessionReady] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [gamification, setGamification] = useState<GamificationConfig | null>(null);
  const [features, setFeatures] = useState<Features>(NO_FEATURES);
  const [endpoints, setEndpoints] = useState<Endpoints>(NO_ENDPOINTS);
  const [limits, setLimits] = useState<Limits>(NO_LIMITS);
  const [analyticsId, setAnalyticsId] = useState('');

  useEffect(() => {
    setReauthTrigger(() => emitToHost('session-expired'));

    async function establish(handoffToken: string) {
      try {
        // Every redeem answers with the current set, so a company added to a
        // whitelist lights up on the next proactive refresh — no reload.
        const facts = await createSession(handoffToken);
        setFeatures(facts.features);
        setEndpoints(facts.endpoints);
        setLimits(facts.limits);
        setAnalyticsId(facts.analyticsId);
        setGamificationCompanyId(facts.companyId);
        setSessionReady(true);
        setStatus('ready');
      } catch (err) {
        // A refresh that failed while the current token still lives changes
        // nothing; the 401 path re-asks when that token runs out.
        if (getSessionToken()) return;
        setSessionReady(false);
        setStatus(err instanceof ApiRequestError && err.retryable ? 'unavailable' : 'error');
      }
    }

    const fragToken = takeHandoffFromFragment();
    if (fragToken) void establish(fragToken);

    // After the fragment read, which owns the fragment, and from the effect
    // rather than render. See scrubQueryFromUrl.
    scrubQueryFromUrl();

    const off = onBridgeMessage((env) => {
      if (env.type === 'init') {
        const payload = env.payload as { handoffToken?: string } | undefined;
        if (payload?.handoffToken) void establish(payload.handoffToken);
      } else if (env.type === 'session-lost') {
        if (reauthRefused()) {
          setSessionReady(false);
          setStatus('expired');
        }
      } else if (env.type === 'set-gamification') {
        const coins = readCoins(env.payload);
        const next = coins ? { coins } : null;
        setGamification(next);
        // Mirror to the module the non-React send path reads.
        setGamificationConfig(next);
      }
    });

    emitToHost('ready');
    return off;
  }, []);

  return (
    <SessionContext.Provider
      value={{ sessionReady, status, gamification, features, endpoints, limits, analyticsId }}
    >
      {children}
    </SessionContext.Provider>
  );
}
